/**
 * JourneysHub — top-level entry into Reclaim's Absorption Engine. (v3 refined)
 *
 * Refinement direction (per user brief):
 *   • Subtle real-world depth (very low-opacity blurred road in backdrop).
 *   • Modern serif tightened, italic body text reduced.
 *   • Glass-effect cards with a soft real-image top strip.
 *   • Gold→sage progress bar (less harsh than gold→rust).
 *   • Softer deeper shadows.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, ChevronRight, Plus, Lock, Share2, Trash2 } from "lucide-react";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_BODY,
  JOURNEY_FONT_LABEL,
} from "../../components/journeys/journeyTheme";
import { useUndoToast } from "../../components/UndoToast";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  // Codebase has inconsistent token key history (`reclaim_token` is canonical
  // but some flows fell back to `token`). Read both to be safe.
  const t = localStorage.getItem("reclaim_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Per-journey hero / strip image. Falls back to a generic warm wash if absent.
const HERO_IMAGE_BY_SLUG = {
  "uk-driving-theory": "/api/journey-images/uk-driving-theory/day1_single_carriageway.png",
};

export default function JourneysHub() {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState(null);
  const [progressBySlug, setProgressBySlug] = useState({});
  const [personalJourneys, setPersonalJourneys] = useState([]);
  const [error, setError] = useState(null);
  const pendingPjDeleteRef = useRef({});
  const { showUndo, toast } = useUndoToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/journeys`, { headers: auth() });
        if (!res.ok) throw new Error("Could not load journeys");
        const data = await res.json();
        if (cancelled) return;
        const list = data.journeys || [];
        setJourneys(list);
        const entries = await Promise.all(
          list.map(async (j) => {
            try {
              const r = await fetch(`${API}/api/journeys/${j.slug}/progress`, { headers: auth() });
              if (!r.ok) return [j.slug, null];
              const p = await r.json();
              return [j.slug, p];
            } catch {
              return [j.slug, null];
            }
          })
        );
        if (cancelled) return;
        setProgressBySlug(Object.fromEntries(entries));
        // Personal journeys (silently ignore errors). Uses fetch but
        // includes the same fallback-key auth as the rest of this hub
        // so iOS Safari sessions never miss the header.
        try {
          const pr = await fetch(`${API}/api/personal-journeys`, { headers: auth() });
          if (pr.ok && !cancelled) {
            const pd = await pr.json();
            setPersonalJourneys(pd.items || []);
          }
        } catch (_) { /* noop */ }
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openJourney = (j) => navigate(`/journeys/${j.slug}`);

  return (
    <div
      className="relative min-h-screen px-6 pt-16 pb-24 overflow-hidden"
      style={{ background: P.bgWash }}
      data-testid="journeys-hub-page"
    >
      {/* ── Subtle real-world depth backdrop on navy ──────────────── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${API}/api/journey-images/uk-driving-theory/day1_awareness.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.10,
          filter: "blur(28px) saturate(0.6) brightness(0.65)",
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(13,27,42,0.78) 0%, rgba(27,42,58,0.92) 100%)",
        }}
        aria-hidden
      />

      <button
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
        className="fixed top-4 left-4 z-50 p-2 rounded-full"
        style={{ background: "rgba(255,255,255,0.10)", border: `1px solid ${P.hairline}`, backdropFilter: "blur(10px)" }}
        data-testid="journeys-hub-back-btn"
        aria-label="Back"
      >
        <ArrowLeft size={16} style={{ color: P.textSecondary }} />
      </button>

      <div className="relative max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p
            className="text-[10px] uppercase tracking-[0.42em] mb-3"
            style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
          >
            Absorption Engine
          </p>
          <h1
            className="text-[34px] leading-[1.08] mb-3"
            style={{
              color: P.textPrimary,
              fontFamily: JOURNEY_FONT_HEAD,
              fontWeight: 400,
              letterSpacing: "-0.012em",
            }}
          >
            A different way to learn.
          </h1>
          <p
            className="text-[14.5px] leading-[1.55] mb-12"
            style={{
              color: P.textSecondary,
              fontFamily: JOURNEY_FONT_BODY,
              fontWeight: 400,
            }}
          >
            No quizzes. No pressure. Just a calm daily flow that lets the material settle into you.
          </p>
        </motion.div>

        {error && (
          <p
            className="text-[13px] text-center"
            style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_BODY }}
          >
            {error}
          </p>
        )}

        {!error && !journeys && (
          <div className="flex justify-center py-20">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: P.accentBlue }} />
          </div>
        )}

        {!error && journeys && journeys.length === 0 && (
          <p
            className="text-[13px] text-center mt-12"
            style={{ color: P.textSecondary, fontFamily: JOURNEY_FONT_BODY }}
          >
            No journeys available yet. Check back soon.
          </p>
        )}

        <div className="space-y-5">
          {journeys?.map((j, idx) => {
            const p = progressBySlug[j.slug];
            const currentDay = (p && p.current_day) || 1;
            const total = j.total_days || 21;
            const pct = Math.min(100, Math.round(((currentDay - 1) / total) * 100));
            const heroImg = HERO_IMAGE_BY_SLUG[j.slug];
            return (
              <motion.button
                key={j.slug}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 + idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileTap={{ scale: 0.985 }}
                whileHover={{ y: -2 }}
                onClick={() => openJourney(j)}
                className="w-full text-left rounded-[22px] overflow-hidden relative"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(22px) saturate(1.2)",
                  WebkitBackdropFilter: "blur(22px) saturate(1.2)",
                  border: `1px solid ${P.hairline}`,
                  boxShadow:
                    "0 14px 40px rgba(0,0,0,0.42), 0 0 0 1px rgba(255,255,255,0.05) inset, 0 1px 0 rgba(255,255,255,0.10) inset",
                }}
                data-testid={`journey-card-${j.slug}`}
              >
                {/* Top real-image strip */}
                {heroImg && (
                  <div
                    className="relative w-full"
                    style={{ height: 78 }}
                  >
                    <img
                      src={`${API}${heroImg}`}
                      alt=""
                      className="absolute inset-0 w-full h-full"
                      style={{ objectFit: "cover", filter: "saturate(0.88) contrast(0.96) brightness(0.85)" }}
                      loading="lazy"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(13,27,42,0) 0%, rgba(13,27,42,0.78) 100%)",
                      }}
                    />
                  </div>
                )}

                <div className="flex items-start gap-4 p-6">
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-[10px] uppercase tracking-[0.4em] mb-2"
                      style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
                    >
                      {p ? `Day ${currentDay} of ${total}` : `${total}-day journey`}
                    </p>
                    <h3
                      className="text-[22px] leading-[1.15] mb-1"
                      style={{
                        color: P.textPrimary,
                        fontFamily: JOURNEY_FONT_HEAD,
                        fontWeight: 400,
                        letterSpacing: "-0.008em",
                      }}
                    >
                      {j.title}
                    </h3>
                    {j.subtitle && (
                      <p
                        className="text-[13px] mb-3"
                        style={{
                          color: P.textSecondary,
                          fontFamily: JOURNEY_FONT_BODY,
                        }}
                      >
                        {j.subtitle}
                      </p>
                    )}

                    {/* Blue progress with soft load animation */}
                    <div
                      className="h-[3px] rounded-full overflow-hidden mt-3"
                      style={{ background: "rgba(255,255,255,0.10)" }}
                    >
                      <motion.div
                        className="h-full rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                        style={{
                          background: `linear-gradient(90deg, ${P.accentBlue} 0%, ${P.accentTeal} 100%)`,
                        }}
                      />
                    </div>
                  </div>
                  <ChevronRight size={18} style={{ color: P.textMuted, marginTop: 4 }} />
                </div>
              </motion.button>
            );
          })}
        </div>

        {journeys && journeys.length > 0 && (
          <p
            className="text-[10.5px] tracking-[0.4em] uppercase text-center mt-16"
            style={{ color: P.textMuted, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
          >
            more journeys arriving soon
          </p>
        )}

        {/* ── PERSONAL JOURNEY CREATE CTA + LIST ─────────────── */}
        <div className="mt-14">
          <p
            className="text-[10px] uppercase tracking-[0.42em] mb-4"
            style={{ color: "#D7BB7A", fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
          >
            Your Own Knowledge
          </p>
          <motion.button
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            whileTap={{ scale: 0.985 }}
            whileHover={{ y: -2 }}
            onClick={() => navigate("/journeys/create")}
            data-testid="journeys-create-cta"
            className="w-full rounded-[20px] flex items-center gap-4 px-5 py-5 text-left"
            style={{
              background: "linear-gradient(135deg, rgba(215, 187, 122, 0.16) 0%, rgba(255,255,255,0.04) 100%)",
              border: "1px solid rgba(215, 187, 122, 0.5)",
              backdropFilter: "blur(14px)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.35), 0 0 30px rgba(215, 187, 122, 0.12)",
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(215, 187, 122, 0.22)", border: "1px solid #D7BB7A" }}
            >
              <Plus size={20} style={{ color: "#F3EDDD" }} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] tracking-[0.36em] uppercase font-medium" style={{ color: "#D7BB7A" }}>
                Create your own
              </p>
              <p className="italic mt-1" style={{ fontFamily: '"Cormorant Garamond", serif', color: "#F3EDDD", fontSize: "16px", lineHeight: 1.25, fontWeight: 500 }}>
                Drop your textbook. Absorb it in 7, 14 or 21 days.
              </p>
            </div>
            <ChevronRight size={16} style={{ color: "rgba(243, 237, 221, 0.5)" }} />
          </motion.button>

          {personalJourneys.length > 0 && (
            <div className="mt-5 space-y-3" data-testid="personal-journeys-list">
              {personalJourneys.slice(0, 4).map((pj) => (
                <PersonalJourneyRow
                  key={pj.slug}
                  pj={pj}
                  onOpen={() => navigate(`/personal-journeys/${pj.slug}/day/1`)}
                  onDelete={() => {
                    // Soft delete with 4 s undo window.
                    const slug = pj.slug;
                    const removed = pj;
                    setPersonalJourneys((arr) => arr.filter((x) => x.slug !== slug));
                    const fire = () => {
                      delete pendingPjDeleteRef.current[slug];
                      fetch(`${API}/api/personal-journeys/${slug}`, {
                        method: "DELETE", headers: auth(),
                      }).then((r) => {
                        if (!r.ok) setPersonalJourneys((arr) => arr.find((x) => x.slug === slug) ? arr : [...arr, removed]);
                      }).catch(() => setPersonalJourneys((arr) => arr.find((x) => x.slug === slug) ? arr : [...arr, removed]));
                    };
                    const onUndo = () => {
                      const t = pendingPjDeleteRef.current[slug];
                      if (t) { clearTimeout(t); delete pendingPjDeleteRef.current[slug]; }
                      setPersonalJourneys((arr) => arr.find((x) => x.slug === slug) ? arr : [...arr, removed]);
                    };
                    pendingPjDeleteRef.current[slug] = setTimeout(fire, 4000);
                    showUndo("Removed", onUndo);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {toast}
    </div>
  );
}

// ─── Personal Journey Row (with delete confirm) ─────────────────────
function PersonalJourneyRow({ pj, onOpen, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (!confirming) return undefined;
    const t = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(t);
  }, [confirming]);
  const tap = (e) => {
    e.stopPropagation();
    if (!confirming) { setConfirming(true); return; }
    setConfirming(false);
    onDelete();
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === "Enter" && onOpen()}
      data-testid={`personal-journey-${pj.slug}`}
      className="w-full rounded-[16px] px-4 py-3 flex items-center gap-3 text-left cursor-pointer"
      style={{
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${P.hairline}`,
      }}
    >
      <div className="flex-1 min-w-0">
        <p className="truncate font-semibold" style={{ color: P.textPrimary, fontSize: "0.92rem" }}>
          {pj.title}
        </p>
        <p className="text-[11px]" style={{ color: P.textMuted, fontFamily: JOURNEY_FONT_LABEL }}>
          {pj.days_total} days
          {pj.share_mode !== "private" ? " · shared" : " · private"}
        </p>
      </div>
      {pj.share_mode === "private" ? (
        <Lock size={12} style={{ color: P.textMuted }} />
      ) : (
        <Share2 size={12} style={{ color: "#D7BB7A" }} />
      )}
      <button
        onClick={tap}
        data-testid={`personal-journey-delete-${pj.slug}`}
        className="w-8 h-8 rounded-full flex items-center justify-center transition-all"
        style={{
          background: confirming ? "rgba(220, 80, 70, 0.28)" : "transparent",
          border: `1px solid ${confirming ? "rgba(220, 80, 70, 0.7)" : "transparent"}`,
        }}
        title={confirming ? "Tap again to confirm" : "Delete"}
        aria-label={confirming ? "Confirm delete" : "Delete journey"}
      >
        <Trash2 size={13} style={{ color: confirming ? "#FCA5A5" : "rgba(243, 237, 221, 0.45)" }} />
      </button>
      <ChevronRight size={14} style={{ color: P.textMuted }} />
    </div>
  );
}

